import { useState } from "react";
import { toast } from "sonner";
import { ksh } from "@/lib/api";

const BOARD_COLUMNS = [
  { key: "pending_payment", label: "Pending Payment", accent: "#F59E0B" },
  { key: "paid", label: "Paid · Queued", accent: "#0EA5E9" },
  { key: "printing", label: "Printing", accent: "#6366F1" },
  { key: "ready", label: "Ready for pickup", accent: "#059669" },
  { key: "completed", label: "Completed", accent: "#525252" },
];

export default function KanbanBoard({ orders, user, canStatus, canDispatch, updateStatus }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const grouped = BOARD_COLUMNS.reduce((acc, c) => {
    acc[c.key] = orders.filter((o) => o.status === c.key);
    return acc;
  }, {});

  const isSuper = user?.role === "super_admin" || user?.role === "admin";

  // What status can this order be moved into?
  const allowedTargets = (o) => {
    if (o.payment_status !== "paid") return []; // payment guard
    const owner = o.assigned_to;
    const targets = [];
    if (canStatus && (isSuper || !owner || owner === user.id)) {
      targets.push("printing", "ready");
    }
    if (canDispatch) {
      // Dispatch can mark completed regardless of who fulfilled
      targets.push("completed");
    }
    return targets;
  };

  const canMoveTo = (o, target) => {
    if (target === o.status) return false;
    return allowedTargets(o).includes(target);
  };

  const advance = (o) => {
    const order = ["paid", "printing", "ready", "completed"];
    const idx = order.indexOf(o.status);
    const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
    if (!next) {
      toast.info("Already at the last stage");
      return;
    }
    if (!canMoveTo(o, next)) {
      toast.error(
        o.payment_status !== "paid"
          ? "Order is unpaid — mark it paid first."
          : "You can't advance this order."
      );
      return;
    }
    updateStatus(o.id, next);
  };

  const handleDrop = (target) => {
    if (!dragId) return;
    const o = orders.find((x) => x.id === dragId);
    setDragId(null);
    setOverCol(null);
    if (!o) return;
    if (!canMoveTo(o, target)) {
      if (o.payment_status !== "paid" && (target === "printing" || target === "ready" || target === "completed")) {
        toast.error("Order is unpaid — mark it paid before moving it into fulfilment.");
      } else if (target === o.status) {
        // no-op
      } else {
        toast.error("That move isn't allowed.");
      }
      return;
    }
    updateStatus(o.id, target);
  };

  return (
    <div className="-mx-2 overflow-x-auto" data-testid="kanban-board">
      <div className="flex gap-3 px-2 min-w-max md:min-w-0 md:grid md:grid-cols-5">
        {BOARD_COLUMNS.map((col) => {
          const items = grouped[col.key] || [];
          const isOver = overCol === col.key;
          return (
            <div
              key={col.key}
              data-testid={`kanban-col-${col.key}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (overCol !== col.key) setOverCol(col.key);
              }}
              onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
              onDrop={() => handleDrop(col.key)}
              className={`bg-[#FAFAFA] border-2 ${isOver ? "border-[#059669]" : "border-[#E5E5E5]"} w-72 md:w-auto flex-shrink-0 flex flex-col`}
            >
              <div
                className="px-3 py-2 border-b-2 flex items-center justify-between"
                style={{ borderColor: col.accent }}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A]">
                  {col.label}
                </div>
                <div className="mono font-display font-black text-lg" style={{ color: col.accent }}>
                  {items.length}
                </div>
              </div>
              <div className="p-2 space-y-2 min-h-[120px] max-h-[70vh] overflow-y-auto">
                {items.length === 0 && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] text-center py-6">
                    Empty
                  </div>
                )}
                {items.map((o) => {
                  const targets = allowedTargets(o);
                  const draggable = targets.length > 0;
                  const isMine = o.assigned_to === user.id;
                  return (
                    <div
                      key={o.id}
                      draggable={draggable}
                      onDragStart={() => setDragId(o.id)}
                      onDragEnd={() => { setDragId(null); setOverCol(null); }}
                      data-testid={`kanban-card-${o.tracking_number}`}
                      className={`bg-white border ${dragId === o.id ? "border-[#059669] shadow-lg" : "border-[#E5E5E5]"} p-3 ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-default"} hover:border-[#0A0A0A] transition-colors`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="mono font-bold text-xs truncate">{o.tracking_number}</div>
                          <div className="font-bold text-sm truncate">{o.customer_name}</div>
                          <div className="text-[11px] text-[#525252] mono truncate">{o.customer_phone}</div>
                        </div>
                        <div className="text-right mono font-bold text-sm whitespace-nowrap">{ksh(o.total)}</div>
                      </div>

                      <div className="mt-2 flex items-center flex-wrap gap-1">
                        {o.tier && o.tier !== "normal" && (
                          <span
                            className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold ${
                              o.tier === "priority"
                                ? "border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]"
                                : "border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]"
                            }`}
                          >
                            {o.tier === "priority" ? "⚡ Priority" : "⚡ Urgent"}
                          </span>
                        )}
                        {o.payment_status !== "paid" && (
                          <span className="px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]">
                            Unpaid
                          </span>
                        )}
                        {o.assigned_to_name && (
                          <span className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold ${isMine ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-[#E5E5E5] text-[#525252]"}`}>
                            {isMine ? "Mine" : o.assigned_to_name}
                          </span>
                        )}
                        <span className="px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold border-[#E5E5E5] text-[#525252]">
                          {o.items?.length || 0} item{(o.items?.length || 0) === 1 ? "" : "s"}
                        </span>
                      </div>

                      {draggable && (
                        <button
                          onClick={() => advance(o)}
                          data-testid={`kanban-advance-${o.tracking_number}`}
                          className="mt-3 w-full md:hidden inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669]"
                        >
                          Advance →
                        </button>
                      )}
                      {!draggable && o.payment_status !== "paid" && (
                        <div className="mt-2 text-[9px] uppercase tracking-[0.15em] text-[#92400E]">
                          Mark paid before fulfilment
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-2 mt-3 text-[10px] uppercase tracking-[0.2em] text-[#525252]">
        Drag cards across columns on desktop · tap <span className="text-[#0A0A0A] font-bold">Advance →</span> on mobile. Unpaid orders are locked.
      </div>
    </div>
  );
}
